import { useQuery, useMutation } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Clock, Loader2, Mail, Send, Siren } from "lucide-react";
import { SiTelegram } from "react-icons/si";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { formatTimeAgo } from "@/lib/ai-engine";
import { reachedAnyone, sinceText, useAckEscalation, useOpenEscalations, type Escalation } from "@/hooks/use-escalations";
import { errorMessage, useWorkspaceRole } from "@/components/content-simple/use-studio";
import { DeliveryLine, LevelChip } from "@/components/escalation-ui";

/** What the Settings page already knows about this workspace's Telegram link (GET /api/telegram/config/:id). */
export interface TelegramView {
  status: "connected" | "waiting_for_start" | "not_configured";
  botUsername?: string;
}

/**
 * The existing Telegram test route. It only reaches Telegram: there is no route that exercises the email fallback, so the button
 * says so rather than promising more than it does.
 */
const TEST_ROUTE = (workspaceId: number) => `/api/telegram/test/${workspaceId}`;

function goToTelegramCard() {
  document.querySelector('[data-testid="section-telegram-cmp"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function AlertRow({ e }: { e: Escalation }) {
  const ack = useAckEscalation();
  const since = sinceText(e.createdAt);
  return (
    <li className="flex flex-col gap-2 rounded-xl border border-border bg-muted/20 p-3 sm:flex-row sm:items-start sm:justify-between" data-testid={`alert-${e.id}`} data-level={e.level}>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <LevelChip level={e.level} />
          <p className="min-w-0 break-words text-sm font-medium text-foreground">{e.title}</p>
        </div>
        <p className="text-[11px] text-muted-foreground">
          {since ? `Since ${since}${e.createdAt ? ` (${formatTimeAgo(e.createdAt)})` : ""}; ` : ""}
          <DeliveryLine escalation={e} tone="plain" />
        </p>
        {e.level !== "info" && !reachedAnyone(e) && (
          <p className="text-[11px] font-medium text-red-400" data-testid={`alert-undelivered-${e.id}`}>Nobody has been told about this yet; delivery is retried automatically.</p>
        )}
        {e.body && <p className="whitespace-pre-wrap break-words text-xs text-muted-foreground">{e.body}</p>}
      </div>
      <button
        type="button"
        onClick={() => ack.mutate(e.id)}
        disabled={ack.isPending}
        className="flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted/40 disabled:pointer-events-none disabled:opacity-50"
        data-testid={`button-alert-ack-${e.id}`}
      >
        {ack.isPending ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Acknowledge
      </button>
    </li>
  );
}

/**
 * Settings -> Notifications: where urgent (P0) alerts go, and what happens when they cannot be delivered.
 * Telegram is the first channel; the workspace owner's email is the fallback. Open alerts of every level are listed here
 * so a P1 or an info note, which have no banner, can still be acknowledged.
 */
export function EscalationSettings({
  workspaceId,
  telegram,
  telegramLoading,
}: {
  workspaceId: number;
  telegram: TelegramView | null;
  telegramLoading: boolean;
}) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { role } = useWorkspaceRole();
  const { escalations, available, isLoading } = useOpenEscalations();

  // The sender view (shared with Settings -> Email) already carries the owner's address.
  const { data: sender } = useQuery<{ ownerEmail?: string | null }>({
    queryKey: ["/api/workspaces", workspaceId, "email-sender"],
    enabled: !!workspaceId,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await fetch(`/api/workspaces/${workspaceId}/email-sender`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });
  const ownerEmail = sender?.ownerEmail ?? (role === "owner" ? user?.email ?? null : null);

  const connected = telegram?.status === "connected";
  const waiting = telegram?.status === "waiting_for_start";
  // The Settings page passes whatever the Telegram config route answered. Only the workspace owner may read it: an admin gets an error body,
  // which has no status, and "Not connected" would then be a claim nobody checked.
  const unknown = !!telegram && !connected && !waiting && telegram.status !== "not_configured";

  const test = useMutation({
    mutationFn: async () => (await apiRequest("POST", TEST_ROUTE(workspaceId), {})).json().catch(() => ({})) as Promise<{ success?: boolean }>,
    onSuccess: (r) =>
      r?.success === false
        ? toast({ title: "The test didn't go out", description: "Telegram didn't accept the message. Check the connection in the Telegram card.", variant: "destructive" })
        : toast({ title: "Test sent to Telegram", description: "It should arrive in your linked chat in a moment." }),
    onError: (e) => toast({ title: "The test wasn't sent", description: errorMessage(e, "Check the Telegram connection."), variant: "destructive" }),
  });

  return (
    <div className="bg-card/50 border border-border rounded-2xl p-4 md:p-6 space-y-5" data-testid="section-urgent-alerts">
      <div className="flex items-center gap-3">
        <Siren size={20} className="text-red-400" />
        <div>
          <h2 className="font-display font-bold text-foreground">Urgent alerts</h2>
          <p className="text-xs text-muted-foreground/70 mt-0.5">What happens when something goes badly wrong and you need to know now.</p>
        </div>
      </div>

      <div className="rounded-xl border border-red-500/30 bg-red-500/5 px-3 py-2.5" data-testid="text-p0-rule">
        <p className="text-sm font-semibold text-foreground">A failed notification is itself a P0.</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground/80">
          If an urgent alert can't be delivered, that failure becomes its own P0. A P0 stays pinned at the top of Content Studio and Draper until you acknowledge it.
        </p>
      </div>

      <div className="space-y-2">
        {/* Telegram */}
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-border bg-muted/20 p-3" data-testid="row-alert-telegram" data-status={telegramLoading ? "loading" : telegram?.status ?? "not_configured"}>
          <div className="flex min-w-0 items-start gap-3">
            <SiTelegram size={16} className="mt-0.5 shrink-0 text-sky-400" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Telegram</p>
              {telegramLoading ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Checking…</p>
              ) : connected ? (
                <p className="flex items-center gap-1.5 text-xs text-emerald-400" data-testid="text-alert-telegram-status">
                  <CheckCircle2 size={12} /> Connected{telegram?.botUsername ? ` as @${telegram.botUsername}` : ""}
                </p>
              ) : waiting ? (
                <p className="flex items-center gap-1.5 text-xs text-amber-400" data-testid="text-alert-telegram-status">
                  <Clock size={12} /> Waiting for you to link it
                </p>
              ) : unknown ? (
                <p className="text-xs text-muted-foreground" data-testid="text-alert-telegram-status">Only the workspace owner can see whether Telegram is connected.</p>
              ) : (
                <p className="flex items-center gap-1.5 text-xs text-amber-400" data-testid="text-alert-telegram-status">
                  <AlertTriangle size={12} /> Not connected
                </p>
              )}
              {!telegramLoading && !connected && !unknown && (
                <p className="mt-1 text-[11px] text-muted-foreground/80">Until Telegram is linked, urgent alerts can only reach you by email.</p>
              )}
            </div>
          </div>
          {!telegramLoading && !connected && !unknown && (
            <button
              type="button"
              onClick={goToTelegramCard}
              className="rounded-lg border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:bg-muted/40"
              data-testid="button-alert-setup-telegram"
            >
              Set up Telegram
            </button>
          )}
        </div>

        {/* Email fallback */}
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/20 p-3" data-testid="row-alert-email">
          <Mail size={16} className="mt-0.5 shrink-0 text-blue-400" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">Email fallback</p>
            <p className="break-all text-xs text-foreground/90" data-testid="text-alert-owner-email">{ownerEmail ?? "The workspace owner's email address"}</p>
            <p className="mt-1 text-[11px] text-muted-foreground/80">Used when Telegram can't be reached, so one broken channel doesn't hide an urgent alert.</p>
          </div>
        </div>
      </div>

      <div className="space-y-1.5">
        <button
          type="button"
          onClick={() => test.mutate()}
          disabled={!connected || test.isPending}
          title={connected ? undefined : unknown ? "Only the workspace owner can send a test" : "Link Telegram first, so the test has somewhere to go"}
          className="flex items-center gap-2 px-4 py-3 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted/40 transition-colors disabled:opacity-50 disabled:pointer-events-none"
          data-testid="button-send-test-notification"
        >
          {test.isPending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send me a test notification
        </button>
        <p className="text-[11px] text-muted-foreground/70" data-testid="text-test-scope">
          {connected ? "Sends a test message to your linked Telegram chat. It does not test the email fallback." : unknown ? "The test goes to the owner's linked Telegram chat, so only the owner can send it." : "Link Telegram first, then you can send yourself a test."}
        </p>
      </div>

      <div className="space-y-2 border-t border-border pt-4" data-testid="section-open-alerts">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-foreground">Open alerts</h3>
          {escalations.length > 0 && <span className="rounded-full bg-red-500/20 px-1.5 text-[11px] font-bold text-red-300">{escalations.length}</span>}
        </div>
        {isLoading ? (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 size={12} className="animate-spin" /> Loading…</p>
        ) : !available ? (
          <p className="text-xs text-muted-foreground" data-testid="text-alerts-unavailable">Urgent alerts aren't available on this server yet.</p>
        ) : escalations.length === 0 ? (
          <p className="text-xs text-muted-foreground" data-testid="text-alerts-none">Nothing needs your attention right now.</p>
        ) : (
          <ul className="space-y-2" data-testid="list-open-alerts">
            {escalations.map((e) => <AlertRow key={String(e.id)} e={e} />)}
          </ul>
        )}
      </div>
    </div>
  );
}
